import { Button } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
import { ContentState, convertToRaw, RawDraftContentState } from "draft-js";
import React, { ReactElement } from "react";
import { Field, Form as FinalForm } from "react-final-form";
import TextareaAutosize from "react-textarea-autosize";
import * as Yup from "yup";
import useIsMobile from "~/lib/hooks/useIsMobile";
import validate from "~/lib/utils/validate";
import Auth from "~/components/auth/Auth";
import { Margin } from "~/components/spacings";
import { FormattedMessage } from "react-intl";
import useIntlValidationMessages from "~/lib/hooks/useIntlValidationMessages";
import { omit } from "lodash/fp";

interface IWriteCommentProps {
  initialComment?: string;
  actionText?: string;
  autoFocus?: boolean;
  onSubmit: (contentState: RawDraftContentState) => void;
  fluid?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export function WriteComment({
  initialComment = "",
  placeholder,
  actionText = "Comment",
  autoFocus = false,
  onSubmit,
  fluid = false,
  disabled = false,
  ...props
}: IWriteCommentProps): ReactElement {
  const isMobile = useIsMobile();
  const intlValidationMessages = useIntlValidationMessages();

  const validationSchema = Yup.object().shape({
    comment: Yup.string().trim().required(intlValidationMessages.cantBeBlank),
  });

  return (
    <Auth>
      {({ user }) => {
        if (!user) {
          return null;
        }

        return (
          <FinalForm
            validate={validate(validationSchema)}
            onSubmit={(values) => {
              if ((values as any).comment) {
                const contentState = ContentState.createFromText((values as any).comment.trim());
                return onSubmit(convertToRaw(contentState));
              }
            }}
            initialValues={{
              comment: initialComment,
            }}
          >
            {({ submitting, form, values, handleSubmit }) => (
              <form
                onSubmit={(event) => {
                  const promise = handleSubmit(event);

                  if (promise instanceof Promise) {
                    promise.then(() =>
                      form.reset({
                        comment: "",
                      }),
                    );
                  }
                }}
              >
                <Field name="comment" parse={(value) => value || ""}>
                  {({ input }) => {
                    if (fluid) {
                      return (
                        <>
                          <TextareaAutosize
                            {...omit(["onFocus"], input)}
                            {...props}
                            minRows={2}
                            placeholder={placeholder}
                            autoFocus={autoFocus}
                            css={{
                              flex: 1,
                              padding: 10,
                              outline: 0,
                              fontSize: 14,
                              borderRadius: 4,
                              width: "100%",
                              border: `solid 1px ${palette.lightestGray}`,
                              resize: "none",
                              "&:focus": {
                                border: `solid 1px ${palette.primary}`,
                              },
                            }}
                          />

                          <Margin mt={10}>
                            <Button
                              width="100%"
                              size="xsmall"
                              theme="primary"
                              color={submitting ? "disabled" : palette.primary}
                              disabled={submitting || !values.comment.trim()}
                              type="submit"
                            >
                              {submitting ? <FormattedMessage defaultMessage="Please wait" id="fomGQV" /> : actionText}
                            </Button>
                          </Margin>
                        </>
                      );
                    }

                    return (
                      <>
                        <div css={{ display: "flex", alignItems: "flex-start" }}>
                          <ProfilePicture
                            size={isMobile ? 32 : 38}
                            image={user.avatar}
                            name={user.fullName}
                            innovator={user.__typename === "GovernmentUser" && user.isInnovator}
                          />

                          <div
                            css={{
                              display: "flex",
                              marginLeft: 10,
                              width: "100%",
                            }}
                          >
                            <TextareaAutosize
                              {...omit(["onFocus"], input)}
                              {...props}
                              minRows={1}
                              placeholder={placeholder}
                              autoFocus={autoFocus}
                              disabled={disabled}
                              css={{
                                flex: 1,
                                padding: 10,
                                outline: 0,
                                fontSize: 14,
                                borderRadius: 4,
                                border: `solid 1px ${palette.lightestGray}`,
                                resize: "none",
                                "&:focus": {
                                  border: `solid 1px ${palette.primary}`,
                                },
                                "&:disabled": {
                                  backgroundColor: palette.washGray,
                                },
                              }}
                            />
                          </div>
                        </div>

                        {values.comment.trim() && (
                          <Margin mt={10}>
                            <Button
                              type="submit"
                              style={{
                                marginLeft: "auto",
                              }}
                              size="xsmall"
                              theme="primary"
                              color={submitting ? "disabled" : palette.primary}
                              disabled={submitting}
                            >
                              {submitting ? <FormattedMessage defaultMessage="Please wait" id="fomGQV" /> : actionText}
                            </Button>
                          </Margin>
                        )}
                      </>
                    );
                  }}
                </Field>
              </form>
            )}
          </FinalForm>
        );
      }}
    </Auth>
  );
}
